/**
 * Phase handlers for the business model strategy
 */

import { StrategyPhase } from '../baseStrategy';
import { generateBusinessModelData } from './utils';
import { BusinessModelOptions, CompanyBizModelOptions } from './components';
import { runAIMatches, runMultiMatches } from '../../api';

/**
 * Handler for the init phase of the business model strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
export const initPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Business model strategy: init phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nInitializing business model discovery...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Make sure matchResults is passed to the decision object
    const updatedDecision = {
      ...decision,
      // If handleRefreshResults is available, use it to get the latest matchResults
      handleRefreshResults: decision.handleRefreshResults
    };

    // Generate business model data
    const { businessModelData, aiMatchesResult } = 
      await generateBusinessModelData(messages, setMessages, updatedDecision, true);

    // Update thinking accordion
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nBusiness model data generated successfully.`
              }
            : msg
        );
        return updatedMessages;
      });
    }

    // Return a decision that will render the business model options component
    return {
      ...decision,
      actionType: 'RENDER_COMPANY_BIZMODEL_OPTIONS',
      component: BusinessModelOptions,
      strategyPhase: StrategyPhase.SUGGESTIONS,
      aiMatchesResult,
      componentProps: {
        businessModelData: {
          ...businessModelData,
          totalCount: aiMatchesResult?.businessModelTotal || 0
        },
        advanceToNextTopic: decision.advanceToNextTopic,
        setMessages,
        onSendText: decision.onSendText,
        messages, // Pass messages to the component
        handleRefreshResults: decision.handleRefreshResults, // Pass handleRefreshResults to the component
        onBusinessModelsSelected: models => {
          return {
            ...decision,
            actionType: 'ADVANCE_TOPIC', // Change to ADVANCE_TOPIC to move to the next topic
            selectedBusinessModels: models
          };
        }
      }
    };
  } catch (error) {
    console.error('Error generating business model options:', error);
    
    // Even on error, don't send to backend - just return the original decision
    return {
      ...decision,
      strategyPhase: StrategyPhase.SUGGESTIONS
    };
  }
};

/**
 * Handler for the suggestions phase of the business model strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
export const suggestionsPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Business model strategy: suggestions phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating business model suggestions...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Generate business model data
    const { businessModelData, aiMatchesResult } = 
      await generateBusinessModelData(messages, setMessages, decision, true);
    
    // Return a decision that will render the business model options component
    return {
      ...decision,
      actionType: 'RENDER_BUSINESS_MODEL_OPTIONS',
      component: BusinessModelOptions,
      strategyPhase: StrategyPhase.CONVERSATION,
      aiMatchesResult,
      componentProps: {
        businessModelData: {
          ...businessModelData,
          totalCount: aiMatchesResult?.businessModelTotal || 0
        },
        advanceToNextTopic: decision.advanceToNextTopic,
        setMessages,
        onSendText: decision.onSendText,
        messages, // Pass messages to the component
        handleRefreshResults: decision.handleRefreshResults, // Pass handleRefreshResults to the component
        onBusinessModelsSelected: models => {
          return {
            ...decision,
            actionType: 'ADVANCE_TOPIC', // Change to ADVANCE_TOPIC to move to the next topic
            selectedBusinessModels: models
          };
        }
      }
    };
  } catch (error) {
    console.error('Error generating business model suggestions:', error);
    
    // Even on error, don't send to backend - just return the original decision
    return {
      ...decision,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Handler for the conversation phase of the business model strategy
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
export const conversationPhaseHandler = async (decision, messages, setMessages) => {
  console.log('Business model strategy: conversation phase');
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nContinuing conversation about business models...`
            }
          : msg
      );
      return updatedMessages;
    });
  }
  
  try {
    // Generate business model data
    const { businessModelData, aiMatchesResult } = 
      await generateBusinessModelData(messages, setMessages, decision, true);
    
    // Return a decision that will render the business model options component
    return {
      ...decision,
      actionType: 'RENDER_BUSINESS_MODEL_OPTIONS',
      component: BusinessModelOptions,
      strategyPhase: StrategyPhase.CONVERSATION,
      aiMatchesResult,
      componentProps: {
        businessModelData: {
          ...businessModelData,
          totalCount: aiMatchesResult?.businessModelTotal || 0
        },
        advanceToNextTopic: decision.advanceToNextTopic,
        setMessages,
        onSendText: decision.onSendText,
        messages, // Pass messages to the component
        handleRefreshResults: decision.handleRefreshResults, // Pass handleRefreshResults to the component
        onBusinessModelsSelected: models => {
          return {
            ...decision,
            actionType: 'ADVANCE_TOPIC', // Change to ADVANCE_TOPIC to move to the next topic
            selectedBusinessModels: models
          };
        }
      }
    };
  } catch (error) {
    console.error('Error in conversation phase:', error);
    
    // Even on error, don't send to backend - just return the original decision
    return {
      ...decision,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

/**
 * Handler for rendering the business model options component
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
export const renderComponentHandler = async (decision, messages, setMessages) => {
  console.log('Business model strategy: render component');
  
  // If we already have the component and business model data, just return the decision
  if (decision.component === BusinessModelOptions && decision.componentProps?.businessModelData) {
    return decision;
  }
  
  // Otherwise, generate the stage data
  try {
    // Generate business model data
    const { businessModelData, aiMatchesResult } = 
      await generateBusinessModelData(messages, setMessages, decision, true);
    
    // Return a decision that will render the business model options component
    return {
      ...decision,
      actionType: 'RENDER_BUSINESS_MODEL_OPTIONS',
      component: BusinessModelOptions,
      aiMatchesResult,
      componentProps: {
        businessModelData: {
          ...businessModelData,
          totalCount: aiMatchesResult?.businessModelTotal || 0
        },
        advanceToNextTopic: decision.advanceToNextTopic,
        setMessages,
        onSendText: decision.onSendText,
        messages, // Pass messages to the component
        handleRefreshResults: decision.handleRefreshResults, // Pass handleRefreshResults to the component
        onBusinessModelsSelected: models => {
          return {
            ...decision,
            actionType: 'ADVANCE_TOPIC', // Change to ADVANCE_TOPIC to move to the next topic
            selectedBusinessModels: models
          };
        }
      }
    };
  } catch (error) {
    console.error('Error generating business model options for component:', error);
    
    // Even on error, don't send to backend - just return the original decision
    return {
      ...decision,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};
